<template>
  <div ref="pdfContent" class="pdf-content">
    <Loader v-if="isLoadingData" fullPage useOverlay loader="spinner"></Loader>
  </div>
</template>

<script>
import Loader from "@/components/Loader/Loader";
import { mapGetters, mapActions, mapState } from "vuex";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { EventBus } from "./eventBus";
import { fnPOST } from "../../../../api/api-methods";
import { APIPATH_SALESFORCE } from "../../../../api/api-paths";
import QRCode from "qrcode";

let APIDomain = new URL(window.location.href);
if(APIDomain.hostname != "localhost"){
  pdfMake.vfs = pdfFonts.pdfMake.vfs
}

export default {
  components: {
    Loader,
    QRCode,
  },
  data() {
    return {
      name: "PrintBox",
      isLoadingData: false,
      projectID: "",
      partNo: 0,
      header: [],
      companyInfo: [],
      mapping_id: null,
      tableData: [],
      lengthData: [],
      imageUrl: "",
      imageSrc: "", // Base64 data of the image
      employee_nik: null,
      employee_name: null,
      countBox: 0,
    };
  },
  computed: {
    ...mapGetters("session", ["userData"]),
  },
  created() {
    this.employee_nik = this.userData.nik;
    this.employee_name = this.userData.fullname;
    this.imgLogo();
    EventBus.$on("open-printbox", (data, index) => {
      console.log(data);
      this.projectID = data;
      this.partNo = index;
      this.loadData();
    });
  },
  beforeDestroy() {
    EventBus.$off("open-printbox");
  },
  methods: {
    totalQty(index) {
      if (index < 0 || index >= this.tableData.length) {
        return 0; // Return 0 if index is out of bounds
      }

      const boxNo = this.header[index].noBox;
      const boxData = this.tableData.find((box) => box.boxNo === boxNo);

      if (!boxData) {
        return 0; // Return 0 if no data found for the box
      }

      return boxData.people.reduce((sum, person) => {
        return (
          sum +
          person.uniforms.reduce((uniformSum, uniform) => {
            return uniformSum + uniform.qty;
          }, 0)
        );
      }, 0);
    },

    setData() {
      this.loadData();
    },
    loadData() {
      this.isLoadingData = true;
      localStorage.removeItem("redirect_url");
      fnPOST(APIPATH_SALESFORCE.getOrderBox, {
        project_id: this.projectID,
        part_no: this.partNo,
        category: "box",
        created_by: this.employee_nik,
        created_by_name: this.employee_name,
      })
        .then((rsp) => {
          this.isLoadingData = false;
          let data = rsp.data.payload;
          this.mapping_id = rsp.data.payload[0].mapping_id;
          if (!Array.isArray(data)) {
            console.error("Expected data.payload to be an array.");
            return;
          }

          let header = [];
          let companyInfo = [];
          let tableData = [];

          let currentBoxNo = null;

          data.forEach((val) => {
            this.countBox = val.box_no;
            if (val.box_no !== currentBoxNo) {
              currentBoxNo = val.box_no;

              // Add header and company info only once per box_no
              header.push({
                noPo: val.no_po,
                noBox: val.box_no,
              });

              companyInfo.push({
                name: val.name_customer,
                address: val.address,
                dimension: val.dimension_box,
              });

              // Initialize a new box entry in tableData
              tableData.push({
                boxNo: val.box_no,
                people: [],
              });
            }

            // Find the entry for the current box number
            let boxEntry = tableData.find(
              (entry) => entry.boxNo === val.box_no
            );

            // If boxEntry is not found, create a new entry
            if (!boxEntry) {
              boxEntry = {
                boxNo: val.box_no,
                people: [],
              };
              tableData.push(boxEntry);
            }

            // Check if the person already exists in the people array
            let person = boxEntry.people.find((p) => p.nik === val.nik);

            if (!person) {
              // If the person does not exist, add a new entry
              person = {
                name: val.name,
                nik: val.nik,
                gender: val.gender,
                level: val.department,
                uniforms: [],
                total: 0,
              };
              boxEntry.people.push(person);
            }

            // Add the uniform data and update the total
            person.uniforms.push({
              article: val.article,
              type: val.item,
              size: val.size,
              qty: parseInt(val.qty),
            });

            person.total += parseInt(val.qty);
          });

          // Set the data properties
          this.header = header;
          this.companyInfo = companyInfo;
          this.tableData = tableData;

          this.generatePdf();

          // console.log("Header:", this.header);
          // console.log("Company Info:", this.companyInfo);
          // console.log("Table Data:", this.tableData);
        })
        .catch((err) => {
          this.isLoadingData = false;
          if (err.response.status !== 401) {
            this.showNotification({
              type: "error",
              message: "Failed, Please Try Again",
            });
          }
        })
        .finally(() => {
          // stop loading
          setTimeout(() => {
            this.isLoadingData = false;
          }, 500);
        });
    },
    imgLogo() {
      let APIDomain = new URL(window.location.href);
      let APIPort = "8005";
      let protocol = "https://";
      let APIUrl = protocol + APIDomain.hostname + ":" + APIPort;
      if (APIDomain.hostname == "localhost") {
        APIUrl = "https://testapps.bbi-apparel.com:8005";
      }
      this.imageUrl = APIUrl + "/media/bbi-logo.png";
    },

    async generateQRCode(text) {
      try {
        let qrCodeDataUrl = await QRCode.toDataURL(text, {
          errorCorrectionLevel: "H",
        });
        return qrCodeDataUrl;
      } catch (err) {
        console.error("Error generating QR code:", err);
        return "";
      }
    },

    async convertImage(url) {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error("Error converting image:", error);
        return null;
      }
    },
    async generatePdf() {
      this.imageSrc = await this.convertImage(this.imageUrl);
      let content = [];
      this.isLoadingData = true;
      let APIDomain = new URL(window.location.href);
      let protocol = "https://";
      if (APIDomain.hostname == "localhost") {
        protocol = "http://";
      }
      let APIUrl = protocol + APIDomain.hostname + "/#/qr-databox?data=";

      for (let a = 0; a < this.header.length; a++) {
        let qrCodeDataUrl = await this.generateQRCode(
          APIUrl +
            this.projectID +
            "///" +
            this.header[a].noBox +
            "///" +
            this.mapping_id
        ); // Replace with actual text
        // Header content
        content.push({
          image: this.imageSrc, // Reference to the logo image
          width: 60, // Adjust width as needed
        });

        content.push({
          table: {
            widths: [130, 1, 55], // Adjust the widths as needed
            body: [
              [
                {
                  text: "PO: " + this.header[a].noPo,
                  style: "header",
                  border: [true, true, true, true], // Top, left, right, bottom
                  alignment: "center",
                },
                {
                  text: "", // Empty cell for spacing
                  style: "header",
                  border: [false, false, false, false], // No border
                  alignment: "center",
                },
                {
                  text: [
                    { text: this.header[a].noBox, style: "header" }, // Use the style for the first part
                    // { text: "/" + this.countBox, fontSize: 5 },
                  ],
                  border: [true, true, true, true], // Top, left, right, bottom
                  alignment: "center",
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 1, // Horizontal line width
            vLineWidth: () => 1, // Vertical line width
            hLineColor: () => "#000000", // Line color
            vLineColor: () => "#000000", // Line color
            paddingLeft: () => 4, // Padding left
            paddingRight: () => 4, // Padding right
          },
          margin: [0, 5, 0, 5], // Adjust margin as needed
        });

        // Company info and barcode section
        content.push({
          columns: [
            {
              width: "70%", // Adjust width as needed
              stack: [
                {
                  text: `${this.companyInfo[a].name}`,
                  style: "companyInfo",
                  margin: [0, 0, 0, 5],
                },
                {
                  text: `${this.companyInfo[a].address}`,
                  style: "companyInfo",
                  margin: [0, 0, 0, 5],
                },
                {
                  text: `TOTAL: ${this.totalQty(a)}`,
                  style: "companyInfo2",
                  margin: [0, 0, 0, 15],
                },
                {
                  text: `BOX DIMESION: ${this.companyInfo[a].dimension}`,
                  style: "companyInfo3",
                  margin: [0, 0, 0, 0],
                },
              ],
            },
            {
              width: "30%",
              stack: [
                {
                  image: qrCodeDataUrl, // Barcode image
                  width: 60, // Adjust height as needed
                  height: 60, // Adjust height as needed
                },
              ],
            },
          ],
          columnGap: 1, // Space between the columns
        });

        // content.push({
        //   table: {
        //     widths: [100, 1, 85], // Adjust the widths to fit within the available space
        //     body: [
        //       [
        //         {
        //           text: "NAMA PRODUK",
        //           style: "header2",
        //           border: [true, true, true, true], // Top, left, right, bottom
        //           alignment: "center",
        //         },
        //         {
        //           text: "",
        //           style: "",
        //           border: [false, false, false, false], // Top, left, right, bottom
        //           alignment: "center",
        //         },
        //         {
        //           text: `TL: ${this.totalQty(a)}`,
        //           style: "header2",
        //           border: [true, true, true, true], // Top, left, right, bottom
        //           alignment: "center",
        //         },
        //       ],
        //     ],
        //   },
        // });

        // Table header
        const tableBody = [
          [
            { text: "NO", style: "header2" },
            { text: "NAMA", style: "header2" },
            { text: "NIK", style: "header2" },
            { text: "L/P", style: "header2" },
            { text: "DEPARTMENT", style: "header2" },
            { text: "ARTIKEL", style: "header2" },
            { text: "SERAGAM", style: "header2" },
            { text: "SIZE", style: "header2" },
            { text: "QTY", style: "header2" },
            { text: "TL", style: "header2" },
          ],
        ];

        // Table content
        this.tableData[a].people.forEach((item, index) => {
          item.uniforms.forEach((uniform, uIndex) => {
            const row = [
              {
                text: uIndex === 0 ? (index + 1).toString() : "",
                rowSpan: uIndex === 0 ? item.uniforms.length : 1,
                style: "rowContent",
              },
              {
                text: uIndex === 0 ? item.name || "" : "",
                rowSpan: uIndex === 0 ? item.uniforms.length : 1,
                style: "rowContent",
              },
              {
                text: uIndex === 0 ? item.nik || "" : "",
                rowSpan: uIndex === 0 ? item.uniforms.length : 1,
                style: "rowContent",
              },
              {
                text: uIndex === 0 ? item.gender || "" : "",
                rowSpan: uIndex === 0 ? item.uniforms.length : 1,
                style: "rowContent",
              },
              {
                text: uIndex === 0 ? item.level || "" : "",
                rowSpan: uIndex === 0 ? item.uniforms.length : 1,
                style: "rowContent",
              },
              { text: uniform.article || "", style: "rowContent1" },
              { text: uniform.type || "", style: "rowContent" },
              { text: uniform.size || "", style: "rowContent" },
              { text: uniform.qty || "", style: "rowContent" },
              {
                text: uIndex === 0 ? item.total || "" : "",
                rowSpan: uIndex === 0 ? item.uniforms.length : 1,
                style: "rowContent",
              },
            ];
            tableBody.push(row);
          });
        });

        // Add table to content
        content.push({
          style: "tableExample",
          table: {
            headerRows: 1,
            widths: ["auto", 30, 20, 8, 32, 25, 30, "*", "*", "*"], // Set fixed width for 'ARTIKEL' (e.g., 50 units)
            body: tableBody,
          },
          layout: {
            hLineWidth: function (i, node) {
              return 1;
            },
            vLineWidth: function (i, node) {
              return 1;
            },
            hLineColor: function (i, node) {
              return "#000000";
            },
            vLineColor: function (i, node) {
              return "#000000";
            },
            paddingLeft: function (i, node) {
              return 1;
            }, // Adjust padding for left side
            paddingRight: function (i, node) {
              return 1;
            }, // Adjust padding for right side
            paddingTop: function (i, node) {
              return 1;
            }, // Adjust padding for top side
            paddingBottom: function (i, node) {
              return 1;
            }, // Adjust padding for bottom side
          },
        });
        if (a < this.header.length - 1) {
          console.log("page");
          // Ensure no page break after the last loop iteration
          content.push({ text: "", pageBreak: "after" });
        }
      }

      const docDefinition = {
        content: content,
        pageSize: { width: 227, height: 312 }, // Custom page size in points (8 cm x 11 cm)
        pageOrientation: "potrait", // Landscape orientation
        pageMargins: [10, 10, 10, 10], // Adjust margins as needed
        styles: {
          header: {
            fontSize: 8,
            bold: true,
          },
          companyInfo: {
            fontSize: 7,
            margin: [0, 2, 0, 2],
          },
          companyInfo2: {
            fontSize: 7,
            bold: true,
            margin: [0, 2, 0, 2],
          },
          companyInfo3: {
            fontSize: 5,
            bold: true,
            margin: [0, 2, 0, 2],
          },
          header2: {
            fillColor: "#f2f2f2",
            fontSize: 5,
            bold: true,
            margin: [0, 0, 0, 0], // No margin
            padding: [1, 1, 1, 1], // Padding: [top, left, bottom, right]
            alignment: "center", // Center align text
          },
          rowContent: {
            fontSize: 6,
            margin: [0, 0, 0, 0], // No margin
            padding: [1, 1, 1, 1], // Padding: [top, left, bottom, right]
          },
          rowContent1: {
            fontSize: 6,
            margin: [0, 0, 0, 0], // No margin
            padding: [1, 1, 1, 1], // Padding: [top, left, bottom, right]
          },
          footerTotal: {
            fontSize: 6,
            bold: true,
            alignment: "right", // Right align text
            fillColor: "#f2f2f2",
          },
          tableExample: {
            fontSize: 6, // Adjust font size for table content
            verticalAlignment: "middle",
            margin: [0, 5, 0, 15],
          },
        },
      };

      pdfMake.createPdf(docDefinition).open();
      this.isLoadingData = false;
    },
  },
};
</script>

<style scoped>
.pdf-content {
  display: block;
}
.header .title {
  display: flex;
  justify-content: space-between;
}
.company-info span,
.company-info p {
  margin: 0;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th,
td {
  border: 1px solid black;
  padding: 5px;
}
th {
  background-color: #f2f2f2;
}
.total {
  text-align: right;
  font-weight: bold;
}
</style>
